import { UploadOutlined } from '@ant-design/icons'
import type { SelectChangeEvent } from '@mui/material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { postWithTokenWithStatus } from 'hooks/http'
import type { FC } from 'react'
import React from 'react'
import type { FormType, SnackbarState } from './types'
import { FORM_TYPE_MAP, FORM_TYPE_OPTIONS } from './types'
import { useGlobalState } from 'contexts/GlobalStateContext'

interface UploadSectionProps {
  token: string
  rootOrganizationId: string
  formType: FormType | ''
  file: File | null
  onFormTypeChange: (formType: FormType | '') => void
  onFileChange: (file: File | null) => void
  onSnackbarChange: (snackbar: SnackbarState) => void
}

const UploadSection: FC<UploadSectionProps> = ({
  token,
  rootOrganizationId,
  formType,
  file,
  onFormTypeChange,
  onFileChange,
  onSnackbarChange,
}) => {
  const { checkSubscriptionTierFeature } = useGlobalState()
  const handleFormTypeChange = (event: SelectChangeEvent) => {
    onFormTypeChange(event.target.value as FormType)
  }

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      onFileChange(event.target.files[0])
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      onFileChange(event.dataTransfer.files[0])
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const handleSubmit = async () => {
    if (!file || !formType || !rootOrganizationId) return

    try {
      const base64Content = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64 = reader.result as string
          resolve(base64.split(',')[1])
        }
        reader.readAsDataURL(file)
      })

      const { data, status } = await postWithTokenWithStatus('forms/upload', token, {
        formType: FORM_TYPE_MAP[formType],
        rootOrganizationId,
        content: base64Content,
      })

      if (status >= 400) {
        throw new Error(data.error || 'Failed to upload form', { cause: data.details })
      }

      onFileChange(null)
      onFormTypeChange('')

      onSnackbarChange({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success',
      })
    } catch (error) {
      onSnackbarChange({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to upload form',
        details: error instanceof Error ? (error.cause as string[] | string) : undefined,
        severity: 'error',
      })
    }
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadOutlined />
            <Typography variant="h6">Import Data</Typography>
          </Box>
        }
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormControl variant="outlined" sx={{ width: '100%' }}>
              <InputLabel shrink>Template Type</InputLabel>
              <Select
                value={formType}
                variant="outlined"
                label="Template Type"
                onChange={handleFormTypeChange}
                displayEmpty
                sx={{ borderRadius: '21px' }}
              >
                {FORM_TYPE_OPTIONS.filter((option) => {
                  if (checkSubscriptionTierFeature('REWARDS.ALL')) {
                    return ![
                      'import-components',
                      'import-targets',
                      'import-team-actuals',
                      'import-employee-actuals',
                    ].includes(option.value)
                  } else {
                    return !['import-rewards', 'import-user-rewards'].includes(option.value)
                  }
                }).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: '15px',
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f0f0f0' },
                width: '100%',
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <input
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
                accept=".xlsx,.xls"
              />
              <label htmlFor="file-upload">
                <Button variant="outlined" component="span" startIcon={<UploadOutlined />} sx={{ mb: 2 }}>
                  Select File
                </Button>
              </label>
              <Typography>
                {file ? `Selected file: ${file.name}` : 'Drag and drop a file here, or click to select a file'}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" disabled={!formType || !file} onClick={handleSubmit}>
              Upload Data
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default UploadSection
